import { formatDate, riskLabel, t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { RiskLevel } from '@repo/shared/schema';
import { Button } from '@repo/ui/button';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';

export const RiskBadge = ({ level }: { level?: string | null }) => {
  useLocale();

  const risk = level ?? RiskLevel.PENDING;
  return (
    <span className="raven-risk" data-risk={risk}>
      {risk === RiskLevel.PENDING ? t('reports.pending') : riskLabel(risk)}
    </span>
  );
};
export const OLink = ({
  to,
  children,
  className = 'raven-link',
  label,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  label?: string;
}) => {
  useLocale();

  const { organizationId } = useParams();
  return (
    <Link aria-label={label} className={className} title={label} to={`/o/${organizationId}${to}`}>
      {children}
    </Link>
  );
};
export const QueryState = ({
  loading,
  error,
  retry,
}: {
  loading?: boolean;
  error?: boolean;
  retry?: () => void;
}) => {
  useLocale();

  if (error) {
    return (
      <div className="raven-panel" role="alert">
        <p>{t('workspace.weCouldnTLoadThisInformation')}</p>
        <Button onClick={retry} variant="outline">
          {t('workspace.tryAgain')}{' '}
        </Button>
      </div>
    );
  }
  return loading ? (
    <div aria-live="polite" className="raven-panel animate-pulse">
      {t('workspace.loading')}{' '}
    </div>
  ) : null;
};
export const dateLabel = (date?: Date | string | null, includeTime = false) =>
  date
    ? formatDate(date, getLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(includeTime ? { hour: 'numeric' as const, minute: '2-digit' as const } : {}),
      })
    : '—';
export const Pagination = ({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) => {
  useLocale();
  return (
    <div className="flex items-center justify-end gap-4 py-2 text-xs">
      <Button disabled={page <= 1} onClick={() => onChange(page - 1)} size="sm" variant="ghost">
        {t('workspace.previous')}{' '}
      </Button>
      <span>{t('count.page', { page, total: Math.max(1, totalPages) })}</span>
      <Button
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        size="sm"
        variant="ghost"
      >
        {t('workspace.next')}{' '}
      </Button>
    </div>
  );
};
